/**
 * Clipboard Utilities
 * 
 * Provides robust clipboard operations with fallbacks for when
 * the Clipboard API is blocked by permissions policy or browser settings.
 */

/**
 * Copy text to clipboard with fallback methods
 * 
 * Method 1: Try modern Clipboard API (navigator.clipboard.writeText)
 * Method 2: Fall back to document.execCommand (deprecated but widely supported)
 * Method 3: Create temporary textarea element (works in all browsers)
 */
export async function copyToClipboard(text: string): Promise<void> {
  // Method 1: Try modern Clipboard API
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      console.warn('Clipboard API failed, trying fallback method:', error);
      // Continue to fallback methods
    }
  }

  // Method 2: Try execCommand (deprecated but still works)
  try {
    const success = fallbackCopyToClipboard(text);
    if (success) {
      return;
    }
  } catch (error) {
    console.warn('execCommand fallback failed:', error);
  }

  // If we get here, all methods failed
  throw new Error('Failed to copy to clipboard. Please copy manually.');
}

/**
 * Fallback clipboard copy using textarea element
 * Works in all browsers, even when Clipboard API is blocked
 */
function fallbackCopyToClipboard(text: string): boolean {
  // Create temporary textarea
  const textarea = document.createElement('textarea');
  
  // Set text and make invisible but keep in DOM
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.width = '2em';
  textarea.style.height = '2em';
  textarea.style.padding = '0';
  textarea.style.border = 'none';
  textarea.style.outline = 'none';
  textarea.style.boxShadow = 'none';
  textarea.style.background = 'transparent';
  textarea.style.opacity = '0';
  textarea.setAttribute('readonly', '');
  textarea.setAttribute('aria-hidden', 'true');
  textarea.setAttribute('tabindex', '-1');
  
  // Add to DOM
  document.body.appendChild(textarea);
  
  // Select text
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  
  // Try to copy
  let success = false;
  try {
    success = document.execCommand('copy');
  } catch (err) {
    console.error('execCommand copy failed:', err);
  }
  
  // Clean up
  document.body.removeChild(textarea);
  
  return success;
}

/**
 * Check if clipboard operations are available
 */
export function isClipboardAvailable(): boolean {
  return !!(navigator.clipboard && navigator.clipboard.writeText);
}

/**
 * Copy to clipboard with user-friendly error handling
 * Returns true if successful, false if failed
 */
export async function copyToClipboardSafe(text: string): Promise<boolean> {
  try {
    await copyToClipboard(text);
    return true;
  } catch (error) {
    console.error('Clipboard copy failed:', error);
    return false;
  }
}
